import type { ReactNode } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type { ReviewComment } from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * "✦ Alternative stories from Jordan's bank" (spec §8.5, conflict-story
 * seed) — a behavioral-specific UI payload, not modeled on
 * `ReviewSubmission` (see A1's note on code-section.tsx). Two purple rows,
 * each with its own independent "Attach to review" toggle, namespaced
 * `alt-story:{submissionId}:{storyId}` — a prefix distinct from `hld-alt:`
 * (system design, one per alt design) and `resource:` (assign toggles), so
 * none of the three can ever collide. The coding `alternative-approach:` key
 * was the fourth until LG-644 persisted it as a `ReviewAttachment` row; the
 * toggles left in this map are the behavioral and system-design ones, which
 * stay local and unpersisted.
 */

const STORIES: Array<{ id: string; name: string; note: string }> = [
  {
    id: 'api-deprecation',
    name: '✦ API deprecation story',
    note: 'Same conflict competency, but with a quantified result (cut partner integration time 60%) — better fit if the interviewer probes measurable impact.',
  },
  {
    id: 'design-system-rollback',
    name: '✦ Design-system rollback',
    note: 'Weaker conflict arc but strong Learning signal — pairs well with this one to cover the missing competency.',
  },
]

export function AlternativeStories(props: {
  submissionId: string
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { submissionId, comments = [], anchorRef } = props
  const { openComposer, attachments, toggleAttachment } =
    useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'alt-stories')

  return (
    <SectionCard
      title="Alternative stories"
      commentCount={sectionComments.length}
      onAddComment={() =>
        openComposer('alt-stories', 'Stories · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      <div className="flex flex-col gap-1.5">
        {STORIES.map((story) => {
          const key = `alt-story:${submissionId}:${story.id}`
          const attached = attachments[key] ?? false
          return (
            <div
              key={story.id}
              className="flex flex-wrap items-center gap-2.5 rounded-lg border px-3 py-2.5"
              style={{
                background:
                  'color-mix(in oklab, var(--cd-purple) 5%, transparent)',
                borderColor:
                  'color-mix(in oklab, var(--cd-purple) 22%, transparent)',
              }}
            >
              <span className="shrink-0 text-[12.5px] font-semibold text-(--cd-text)">
                {story.name}
              </span>
              <span className="min-w-[220px] flex-1 text-xs leading-relaxed text-(--cd-text-muted)">
                {story.note}
              </span>
              <button
                type="button"
                onClick={() => toggleAttachment(key)}
                className="shrink-0 rounded-full border px-3 py-1 text-[11.5px] font-semibold"
                style={
                  attached
                    ? {
                        background:
                          'color-mix(in oklab, var(--cd-green) 14%, transparent)',
                        color: 'var(--cd-green)',
                        borderColor:
                          'color-mix(in oklab, var(--cd-green) 40%, transparent)',
                      }
                    : {
                        background: 'transparent',
                        color: 'var(--cd-text-muted)',
                        borderColor: 'var(--cd-border-2)',
                      }
                }
              >
                {attached ? '✓ Attached to review' : 'Attach to review'}
              </button>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}
